import React, { useState, useEffect } from 'react';
import { View, StyleSheet, LayoutChangeEvent } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  runOnJS,
} from 'react-native-reanimated';

interface CustomSliderProps {
  minimumValue: number;
  maximumValue: number;
  value: number;
  onSlidingComplete: (value: number) => void;
  minimumTrackTintColor?: string;
  maximumTrackTintColor?: string;
  thumbTintColor?: string;
  style?: any;
}

export default function CustomSlider({
  minimumValue,
  maximumValue,
  value,
  onSlidingComplete,
  minimumTrackTintColor = '#151515',
  maximumTrackTintColor = 'rgba(0, 0, 0, 0.1)',
  thumbTintColor = '#151515',
  style,
}: CustomSliderProps) {
  const [sliderWidth, setSliderWidth] = useState(0);
  const isDragging = useSharedValue(false);
  const dragPosition = useSharedValue(0);
  const currentProgress = useSharedValue(0);

  useEffect(() => {
    if (!isDragging.value && sliderWidth > 0) {
      const range = maximumValue - minimumValue;
      if (range > 0) {
        currentProgress.value = Math.max(0, Math.min(1, (value - minimumValue) / range));
      } else {
        currentProgress.value = 0;
      }
    }
  }, [value, minimumValue, maximumValue, sliderWidth]);

  const handleLayout = (event: LayoutChangeEvent) => {
    setSliderWidth(event.nativeEvent.layout.width);
  };

  const handleSeekComplete = (finalValue: number) => {
    onSlidingComplete(finalValue);
  };

  const panGesture = Gesture.Pan()
    .onStart(() => {
      if (sliderWidth > 0) {
        dragPosition.value = currentProgress.value * sliderWidth;
      }
      isDragging.value = true;
    })
    .onUpdate((event) => {
      if (sliderWidth <= 0) return;
      const clampedX = Math.max(0, Math.min(event.x, sliderWidth));
      dragPosition.value = clampedX;
    })
    .onEnd((event) => {
      if (sliderWidth <= 0) {
        isDragging.value = false;
        return;
      }
      const clampedX = Math.max(0, Math.min(event.x, sliderWidth));
      const progressValue = clampedX / sliderWidth;
      currentProgress.value = progressValue;
      isDragging.value = false;
      
      const range = maximumValue - minimumValue;
      if (range > 0) {
        const newValue = minimumValue + progressValue * range;
        const finalValue = Math.max(minimumValue, Math.min(maximumValue, newValue));
        runOnJS(handleSeekComplete)(finalValue);
      } else {
        runOnJS(handleSeekComplete)(minimumValue);
      }
    });

  const tapGesture = Gesture.Tap().onEnd((event) => {
    if (sliderWidth <= 0) return;
    const clampedX = Math.max(0, Math.min(event.x, sliderWidth));
    const progressValue = clampedX / sliderWidth;
    currentProgress.value = progressValue;
    
    const range = maximumValue - minimumValue;
    if (range > 0) {
      const newValue = minimumValue + progressValue * range;
      const finalValue = Math.max(minimumValue, Math.min(maximumValue, newValue));
      runOnJS(handleSeekComplete)(finalValue);
    }
  });

  const gesture = Gesture.Simultaneous(panGesture, tapGesture);

  const thumbStyle = useAnimatedStyle(() => {
    if (sliderWidth <= 0) {
      return {
        transform: [{ translateX: 0 }],
      };
    }

    const position = isDragging.value 
      ? dragPosition.value 
      : currentProgress.value * sliderWidth;
      
    return {
      transform: [{ translateX: position }],
    };
  });

  const fillStyle = useAnimatedStyle(() => {
    if (sliderWidth <= 0) {
      return {
        width: 0,
      };
    }

    const width = isDragging.value
      ? dragPosition.value
      : currentProgress.value * sliderWidth;

    return {
      width: Math.max(0, Math.min(sliderWidth, width)),
    };
  });

  return (
    <GestureDetector gesture={gesture}>
      <View style={[styles.container, style]} onLayout={handleLayout}>
        <View style={[styles.track, { backgroundColor: maximumTrackTintColor }]}>
          <Animated.View
            style={[
              styles.fill,
              fillStyle,
              { backgroundColor: minimumTrackTintColor },
            ]}
          />
        </View>
        <Animated.View
          style={[styles.thumb, thumbStyle, { backgroundColor: thumbTintColor }]}
        />
      </View>
    </GestureDetector>
  );
}

const styles = StyleSheet.create({
  container: {
    height: 20,
    justifyContent: 'center',
    position: 'relative',
  },
  track: {
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    borderRadius: 2,
  },
  thumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    position: 'absolute',
    top: 0,
    left: -10,
  },
});
